<template>
  <c-modal
    class-name="m-issue-inventory-item"
    :active="active"
    hide-header
    :disable-scroll="disableScroll"
    @close="close"
  >
    <div class="issue-item">
      <div class="modal-frame__header">
        <h2 class="modal-frame__title">
          Выдать ТМЦ <span class="font-weight-normal">№{{ item && item.serialNumber }}</span>
          <div v-if="item && item.name" class="item-name">{{ item.name }}</div>
        </h2>
      </div>

      <div class="issue-item-form">
        <div class="d-flex flex-wrap">
          <div class="form-group issue-item-form__date" :class="{ 'form-group--invalid': isSubmitted && dateError }">
            <c-input
              ref="calendar"
              v-model="form.date"
              label="Дата выдачи"
              type="date"
              :required-mark="true"
              :clearable="true"
              :required="isSubmitted && dateError"
              :required-error="isSubmitted && dateError"
              format="DD.MM.YYYY, dddd"
              :editable="true"
              icon="calendar"
              placeholder="00.00.0000"
              :icon-right="true"
            />
          </div>
          <div class="form-group issue-item-form__actor">
            <c-input
              v-model="form.actor"
              type="select"
              floating-label="Кто выдает"
              :searchable="true"
              :clearable="true"
              :items="actors"
            />
          </div>
        </div>

        <div class="form-group issue-item-form__worker" :class="{ 'form-group--invalid': isSubmitted && workerError }">
          <c-input
            v-model="form.worker"
            type="select"
            floating-label="Кому выдается"
            :searchable="true"
            :clearable="true"
            :items="workers"
            :required-mark="true"
            :required="isSubmitted && workerError"
            :required-condition="isSubmitted && workerError"
          />
        </div>

        <div class="form-group inventory-item-form__comment">
          <c-textarea
            v-model="form.comment"
            type="textarea"
            :clearable="true"
            floating-label="Комментарий"
            max-length="500"
          />
        </div>

        <div class="form-group inventory-item-form__loader">
          <file-loader ref="loader" v-model="files" class="" format="all" :limit="10" />
        </div>
      </div>
    </div>

    <template #actions>
      <c-button class="issue-item__cancel" label="Отменить" @click="close" />
      <c-button class="issue-item__submit" color="primary" label="Выдать" :pending="pending" @click="submit" />
    </template>
  </c-modal>
</template>

<script>
import moment from "moment";

import CModal from "@/components/scrollable-modal";
import Textarea from "@/components/textarea";
import Input from "@/components/controls2/Input";
import Button from "@/components/controls2/Button";
import FileLoader from "@/components/file-loader";
import { ACTION_ISSUE } from "../constants";
import { DATE_FORMAT_ERROR, REQUIRED_ERROR } from "@/constants/errorFieldValues";
import { isDateValid } from "@/utils/date/valid";
import { Breakpoint } from "@/utils/browser/breakpoint";

export default {
  components: {
    "c-textarea": Textarea,
    "c-input": Input,
    "c-button": Button,
    "file-loader": FileLoader,
    CModal,
  },

  data() {
    return {
      currentUser: null,
      active: false,
      isSubmitted: false,
      pending: false,
      isDesktop: false,
      breakpoint: null,

      item: null,

      form: {
        actor: null,
        date: "",
        worker: null,
        comment: "",
      },

      files: [],
    };
  },

  computed: {
    workers() {
      return this.$store.getters["actors/workersList"];
    },
    actors() {
      return this.$store.getters["actors/activeActorsList"];
    },
    dateError() {
      if (!this.form.date) return REQUIRED_ERROR;
      return isDateValid(this.form.date) ? null : DATE_FORMAT_ERROR;
    },
    workerError() {
      return this.form.worker ? null : REQUIRED_ERROR;
    },
    isValid() {
      return !this.dateError && !this.workerError;
    },
    disableScroll() {
      if (!this.isDesktop) return false;
      return !this.files.length;
    },
  },

  watch: {
    active(v) {
      if (!v) this.reset();
    },
  },

  created() {
    this.currentUser = this.$store.state.userId;
    this.breakpoint = Breakpoint("(min-width: 700px)");
    const cb = (isDesktop) => {
      this.isDesktop = isDesktop;
    };
    this.breakpoint.subscribe(cb);
  },

  beforeUnmount() {
    this.breakpoint.dispose();
    this.breakpoint = null;
  },

  methods: {
    open(item) {
      this.item = item;

      this.form.date = moment().format("DD.MM.YYYY");
      const actor = this.actors.find((a) => {
        if (!a.userId) return false;
        return a.userId.toString() === this.currentUser;
      });
      this.form.actor = actor;
      this.active = true;
    },
    close() {
      this.active = false;
      this.$emit("close");
      this.reset();
    },
    reset() {
      this.item = null;
      this.isSubmitted = false;

      this.pending = false;

      this.form.actor = null;
      this.form.worker = null;
      this.form.comment = "";
      this.form.date = "";
      this.files = [];
      if (this.$refs.loader) this.$refs.loader.reset();
    },
    getParams() {
      const { actor, date, worker, comment } = this.form;
      return {
        action: ACTION_ISSUE,
        inventory_id: this.item.id,
        author_user_id: actor && actor.userId,
        author_worker_id: actor && actor.workerId,
        date,
        worker_id: worker && worker.value,
        comment,

        files: this.files,
      };
    },
    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return false;
      this.pending = true;

      const params = this.getParams();
      this.$store
        .dispatch("uniformItems/action", params)
        .then((res) => {
          if (!res.success) {
            console.error(res.message);
          } else {
            const nameText = this.item.name ? `(${this.item.name})` : "";
            this.$notify({
              group: "success-message",
              data: {
                textMessage: `Позиция <b>№${this.item.serialNumber} ${nameText}</b> успешно выдана сотруднику ${this.form.worker.label}`,
              },
            });

            this.$emit("issued", res.model);

            this.close();
          }
        })
        .catch((err) => {
          console.error(err);
          this.$notify({
            group: "error-message",
            data: {
              textMessage: "Произошла ошибка. Попробуйте ещё раз.",
            },
          });
        })
        .finally(() => {
          this.pending = false;
        });
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.issue-item {
  padding: 0 2px;

  .item-name {
    font-weight: 400;
    font-size: 13px;
    margin-top: 5px;
  }

  &-form {
    .form-group {
      margin-bottom: 24px;

      &--invalid {
        padding-bottom: 35px;
      }
    }

    &__date {
      max-width: 125px;
      margin-right: 30px;
      padding-top: 6px;
    }

    &__actor {
      flex-grow: 1;
    }
  }

  @include onlyMobile {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    overflow: hidden;

    &-form {
      padding-bottom: 60px;
      &__date {
        max-width: 125px;
      }
      &__actor {
        width: 100%;
      }
    }
  }
}
</style>
<style lang="scss">
.m-issue-inventory-item {
  max-width: 590px !important;
}
</style>
